import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  formatPercent,
  formatPercentWithColor,
  getAccuracyColor,
  clickableRow,
  TYPOGRAPHY,
} from '../../theme/forecastStyles';
import type { StockHistoryStocksResponse } from '../../lib/api/stock-history';

interface PerSymbolTableProps {
  stocks: StockHistoryStocksResponse;
  onSymbolClick: (symbol: string) => void;
}

export default function PerSymbolTable({ stocks, onSymbolClick }: PerSymbolTableProps) {
  return (
    <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Symbol Performance Snapshot
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Per-symbol forecast track record (7d accuracy & avg returns).
            </Typography>
          </Box>
        </Box>

        {stocks.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer>
              <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Symbol</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Signals</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>7d Complete</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Accuracy</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Avg 7d Return</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocks.map((s) => {
                  const accColor = getAccuracyColor(s.accuracy_7d);
                  const { text: retText, color: retColor } = formatPercentWithColor(s.avg_return_7d);
                  return (
                    <TableRow
                      key={s.symbol}
                      role="button"
                      tabIndex={0}
                      sx={clickableRow}
                      onClick={() => onSymbolClick(s.symbol)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSymbolClick(s.symbol);
                        }
                      }}
                    >
                      <TableCell sx={{ fontSize: TYPOGRAPHY.tiny, fontWeight: 600, color: 'text.primary' }}>
                        {s.symbol}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
                        {s.signal_count}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
                        {s.completed_7d}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: TYPOGRAPHY.tiny, color: accColor }}>
                        {formatPercent(s.accuracy_7d)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: TYPOGRAPHY.tiny }}>
                        <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: retColor }}>
                          {retText}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
            No per-symbol stats yet. This will populate as forecasts accumulate.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
